// src/services/subscriptionService.js - COMPLETE ENHANCED VERSION
// Handles all subscription operations with robust fallback mechanisms + Event Payments

const subscriptionService = {
  
  // =====================================================
  // EXISTING CORE SUBSCRIPTION METHODS (UNCHANGED)
  // =====================================================

  // added on 1-26-26
  // 
  /**
   * Create checkout session for pay-per-event pass
   */
   /**
 * Create checkout session for pay-per-event pass
 */
async createPayPerEventCheckout(email) {
  try {
    console.log('🎫 Creating pay-per-event checkout for:', email);

    // Build success/cancel URLs
    const successUrl = `${window.location.origin}/subscription/pay-per-event-success?session_id={CHECKOUT_SESSION_ID}`;
    const cancelUrl = `${window.location.origin}/subscription?canceled=true`;

    // 🔥 FIX: Use the same URL pattern as all other methods
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-management`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        action: 'create_pay_per_event_checkout',
        email: email,
        successUrl: successUrl,
        cancelUrl: cancelUrl
      })
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('❌ Pay-per-event checkout failed:', data);
      return {
        success: false,
        error: data.error || 'Failed to create checkout'
      };
    }

    console.log('✅ Pay-per-event checkout created successfully');
    return {
      success: true,
      checkoutUrl: data.checkoutUrl,
      sessionId: data.sessionId
    };

  } catch (error) {
    console.error('❌ Pay-per-event checkout error:', error);
    return {
      success: false,
      error: 'Network error. Please try again.'
    };
  }
},

  /**
   * Check if user has unused pay-per-event passes
   */
  async checkEventPassAvailability(email) {
    console.log('🔍 Checking event pass availability for:', email);
    
    try {
      if (!email) {
        throw new Error('Email is required');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'check_event_pass_availability',
          email: email
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to check pass availability');
      }

      console.log('✅ Pass availability checked:', {
        hasPass: result.has_unused_passes,
        passCount: result.unused_pass_count
      });
      
      return result;
      
    } catch (error) {
      console.error('❌ Error checking pass availability:', error);
      return {
        success: false,
        error: error.message,
        has_unused_passes: false,
        unused_pass_count: 0
      };
    }
  },

  /**
   * Use an event pass for a specific event
   */
  async useEventPass(email, eventSlug, eventTitle, eventDate) {
    console.log('🎫 Using event pass for:', { email, eventSlug });
    
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'use_event_pass',
          email: email,
          eventSlug: eventSlug,
          eventTitle: eventTitle,
          eventDate: eventDate
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to use event pass');
      }

      console.log('✅ Event pass used successfully');
      return result;
      
    } catch (error) {
      console.error('❌ Error using event pass:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // end 1-26-26
  //
  
  /**
   * Get available subscription plans
   */
  async getPlans() {
    console.log('📋 Fetching subscription plans...');
    
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'get_plans'
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to fetch plans');
      }

      console.log('✅ Plans fetched successfully:', result.plans?.length || 0, 'plans');
      return result;
      
    } catch (error) {
      console.error('❌ Error fetching plans:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch subscription plans',
        plans: []
      };
    }
  },

  /**
   * Create Stripe checkout session for subscription
   */
  async createCheckoutSession(email, planCode) {
    console.log('🛒 Creating checkout session:', { email, planCode });
    
    try {
      if (!email || !planCode) {
        throw new Error('Email and plan code are required');
      }

      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
      if (!emailRegex.test(email)) {
        throw new Error('Invalid email address');
      }

      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/subscription/success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${baseUrl}/subscription/cancel`;

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'create_checkout_session',
          email: email,
          planCode: planCode,
          successUrl: successUrl,
          cancelUrl: cancelUrl
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to create checkout session');
      }

      console.log('✅ Checkout session created:', result.session_id);
      return {
        success: true,
        checkoutUrl: result.checkout_url,
        sessionId: result.session_id
      };
      
    } catch (error) {
      console.error('❌ Error creating checkout session:', error);
      return {
        success: false,
        error: error.message || 'Failed to create checkout session'
      };
    }
  },

  /**
   * Get user subscription status
   */
  async getSubscriptionStatus(email) {
    console.log('🔍 Getting subscription status for:', email);
    
    try {
      if (!email) {
        throw new Error('Email is required');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'get_subscription_status',
          email: email
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to get subscription status');
      }

      console.log('✅ Subscription status retrieved:', {
        hasActiveSubscription: result.hasActiveSubscription,
        subscriptionId: result.subscription?.id
      });
      
      return result;
      
    } catch (error) {
      console.error('❌ Error getting subscription status:', error);
      return {
        success: false,
        error: error.message || 'Failed to get subscription status',
        hasActiveSubscription: false,
        subscription: null
      };
    }
  },

  /**
   * Cancel user subscription
   */
  async cancelSubscription(email) {
    console.log('❌ Canceling subscription for:', email);
    
    try {
      if (!email) {
        throw new Error('Email is required');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'cancel_subscription',
          email: email
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to cancel subscription');
      }

      console.log('✅ Subscription canceled:', result.message);
      return result;
      
    } catch (error) {
      console.error('❌ Error canceling subscription:', error);
      return {
        success: false,
        error: error.message || 'Failed to cancel subscription'
      };
    }
  },

  // =====================================================
  // 🆕 NEW: EVENT PAYMENT METHODS
  // =====================================================

  /**
   * Create checkout session for individual event payment
   */
  async createEventCheckoutSession(email, priceCents, currency, eventMetadata) {
    console.log('🎫 Creating event checkout session:', { email, priceCents, currency });
    
    try {
      if (!email || !priceCents) {
        throw new Error('Email and price are required');
      }

      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
      if (!emailRegex.test(email)) {
        throw new Error('Invalid email address');
      }

      const baseUrl = window.location.origin;
      const eventSlug = eventMetadata.event_slug || 'event';
      const successUrl = `${baseUrl}/events/${eventSlug}?payment=success&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${baseUrl}/events/${eventSlug}?payment=cancelled`;

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'create_event_checkout_session',
          email: email,
          priceCents: priceCents,
          currency: currency || 'JPY',
          eventMetadata: eventMetadata,
          successUrl: successUrl,
          cancelUrl: cancelUrl
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to create event checkout session');
      }

      console.log('✅ Event checkout session created:', result.session_id);
      return {
        success: true,
        checkoutUrl: result.checkout_url,
        sessionId: result.session_id
      };
      
    } catch (error) {
      console.error('❌ Error creating event checkout session:', error);
      return {
        success: false,
        error: error.message || 'Failed to create event checkout session'
      };
    }
  },

  /**
   * Check if user has access to specific event
   */
  async checkEventAccess(email, eventSlug, eventMetadata = {}) {
    console.log('🎯 Checking event access for:', { email, eventSlug });
    
    try {
      if (!email || !eventSlug) {
        throw new Error('Email and event slug are required');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'check_event_access',
          email: email,
          eventSlug: eventSlug,
          eventMetadata: eventMetadata
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to check event access');
      }

      console.log('✅ Event access checked:', {
        hasAccess: result.hasAccess,
        accessType: result.accessType,
        requiresPayment: result.requiresPayment
      });
      
      return result;
      
    } catch (error) {
      console.error('❌ Error checking event access:', error);
      return {
        success: false,
        error: error.message || 'Failed to check event access',
        hasAccess: false,
        requiresPayment: false
      };
    }
  },

  // =====================================================
  // EXISTING UTILITY METHODS (UNCHANGED)
  // =====================================================

  /**
   * Format price from cents to currency display
   */
  origformatPrice(cents, currency = 'USD') {
    const amount = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  },

  formatPrice(cents) {
        return new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency: 'JPY',
          minimumFractionDigits: 0, // JPY doesn't use decimal places
          maximumFractionDigits: 0
      }).format(cents / 100);
    },

  /**
   * Calculate savings between monthly and yearly plans
   */
  calculateSavings(monthlyPriceCents, yearlyPriceCents) {
    const monthlyAnnualCents = monthlyPriceCents * 12;
    const savingsCents = monthlyAnnualCents - yearlyPriceCents;
    const savingsPercent = Math.round((savingsCents / monthlyAnnualCents) * 100);
    
    return {
      savingsAmount: this.formatPrice(savingsCents),
      savingsPercent: savingsPercent,
      monthlyAnnualTotal: this.formatPrice(monthlyAnnualCents)
    };
  },

  /**
   * Store email for checkout process
   */
  storeCheckoutEmail(email) {
    try {
      localStorage.setItem('connectco_checkout_email', email);
      console.log('📧 Email stored for checkout:', email);
    } catch (error) {
      console.warn('⚠️ Could not store email in localStorage:', error);
    }
  },

  /**
   * Get stored email from checkout process
   */
  getStoredEmail() {
    try {
      const email = localStorage.getItem('connectco_checkout_email');
      console.log('📧 Retrieved stored email:', email);
      return email;
    } catch (error) {
      console.warn('⚠️ Could not retrieve email from localStorage:', error);
      return null;
    }
  },

  /**
   * Clear stored email after successful processing
   */
  clearStoredEmail() {
    try {
      localStorage.removeItem('connectco_checkout_email');
      console.log('🗑️ Stored email cleared');
    } catch (error) {
      console.warn('⚠️ Could not clear stored email:', error);
    }
  },

  // =====================================================
  // 🆕 NEW: EVENT ACCESS UTILITIES
  // =====================================================

  /**
   * Check if event requires payment based on configuration
   */
  isEventPaymentRequired(eventData) {
    // Default payment settings (this could be imported from your config)
    const paymentSettings = {
      globalPaymentEnabled: false,
      eventTypeDefaults: {
        'speed-fika-connection': {
          paymentEnabled: false,
          requiresSubscription: false
        }
      }
    };

    // Check global setting first
    if (!paymentSettings.globalPaymentEnabled) {
      return false;
    }

    // Check event-specific setting
    if (eventData.paymentEnabled !== undefined) {
      return eventData.paymentEnabled;
    }

    // Check event type defaults
    const typeDefaults = paymentSettings.eventTypeDefaults[eventData.eventType];
    return typeDefaults?.paymentEnabled || false;
  },

  /**
   * Get event pricing information
   */
  getEventPricing(eventData) {
    const defaultPriceCents = 3000; // 3000  Yen default
    
    return {
      perEventPriceCents: eventData.perEventPriceCents || defaultPriceCents,
      currency: eventData.currency || 'JPY',
      requiresSubscription: eventData.requiresSubscription || false,
      paymentOptions: eventData.paymentOptions || {
        allowSubscription: true,
        allowPerEventPayment: true,
        allowWaitlist: true
      }
    };
  },

  /**
   * Generate appropriate button text based on access level
   */
  getEventButtonText(hasSubscription, requiresPayment, isFull) {
    if (hasSubscription) {
      return isFull ? 'Priority Access (Skip Waitlist)' : 'Reserve Your Spot';
    }
    
    if (requiresPayment) {
      return isFull ? 'Pay to Skip Waitlist' : 'Reserve Your Spot';
    }
    
    return isFull ? 'Join Waitlist' : 'Reserve Your Spot';
  },

  // =====================================================
  // EXISTING ROBUST FALLBACK METHODS (UNCHANGED)
  // =====================================================

  /**
   * ROBUST ADDITION: Create fallback subscription when webhook fails
   * This method retrieves the Stripe session and creates the subscription record manually
   */
  async createFallbackSubscription(email, sessionId) {
      console.log('🚨 Creating fallback subscription for:', { email, sessionId });
      
      try {
        // FIXED: Better parameter validation
        if (!email) {
          throw new Error('Email is required for fallback subscription');
        }
        
        if (!sessionId) {
          throw new Error('Session ID is required for fallback subscription. Please contact support.');
        }
    
        // FIXED: Validate email format
        const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        if (!emailRegex.test(email)) {
          throw new Error('Invalid email format');
        }
    
        console.log('📡 Making fallback request...');
        console.log('🌐 URL:', `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-management`);
        console.log('📋 Payload:', { 
          action: 'create_fallback_subscription', 
          email, 
          sessionId 
        });
    
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-management`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            action: 'create_fallback_subscription',
            email: email,
            sessionId: sessionId  // FIXED: Ensure consistent parameter naming
          }),
        });
    
        console.log('📊 Response status:', response.status);
        console.log('📊 Response headers:', Object.fromEntries(response.headers.entries()));
        
        // FIXED: Better error handling for non-200 responses
        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ HTTP Error Response:', errorText);
          
          let errorMessage;
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.error || errorJson.message || 'Unknown server error';
          } catch {
            errorMessage = `Server error (${response.status}): ${errorText}`;
          }
          
          throw new Error(errorMessage);
        }
    
        const result = await response.json();
        console.log('📊 Response result:', result);
        
        if (!result.success) {
          throw new Error(result.error || 'Fallback subscription creation failed');
        }
    
        console.log('✅ Fallback subscription created:', result);
        return result;
        
      } catch (error) {
        console.error('❌ Fallback subscription error:', error);
        
        // FIXED: Better error messaging
        let friendlyMessage = error.message;
        
        if (error.message.includes('sessionId is not defined')) {
          friendlyMessage = 'Session information is missing. Please try again or contact support.';
        } else if (error.message.includes('Failed to fetch')) {
          friendlyMessage = 'Network error. Please check your connection and try again.';
        } else if (error.message.includes('500')) {
          friendlyMessage = 'Server error. Please try again in a moment or contact support.';
        }
        
        return {
          success: false,
          error: friendlyMessage,
          originalError: error.message,
          timestamp: new Date().toISOString()
        };
      }
    },

  /**
   * ROBUST ADDITION: Enhanced subscription status check with retry logic
   * Includes additional debugging information
   */
  async getSubscriptionStatusWithRetry(email, maxRetries = 3) {
    console.log('🔍 Getting subscription status with retry for:', email);
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      console.log(`🔄 Attempt ${attempt}/${maxRetries}`);
      
      try {
        const result = await this.getSubscriptionStatus(email);
        
        if (result.success && result.subscription) {
          console.log('✅ Subscription found on attempt:', attempt);
          return result;
        }
        
        if (attempt < maxRetries) {
          console.log(`⏳ No subscription found, waiting before retry...`);
          await new Promise(resolve => setTimeout(resolve, 2000 * attempt)); // Exponential backoff
        }
        
      } catch (error) {
        console.error(`❌ Attempt ${attempt} failed:`, error);
        if (attempt === maxRetries) {
          return {
            success: false,
            error: error.message,
            hasActiveSubscription: false,
            subscription: null
          };
        }
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
      }
    }
    
    return {
      success: false,
      error: 'Subscription not found after all retries',
      hasActiveSubscription: false,
      subscription: null
    };
  },

  /**
   * ROBUST ADDITION: Verify webhook processing status
   * Checks if webhook has been processed for a given session
   */
  async verifyWebhookProcessing(sessionId) {
    console.log('🔍 Verifying webhook processing for session:', sessionId);
    
    try {
      if (!sessionId) {
        throw new Error('Session ID is required for webhook verification');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'verify_webhook_processing',
          sessionId: sessionId
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Webhook verification failed');
      }

      console.log('🔍 Webhook processing status:', {
        processed: result.webhook_processed,
        eventsFound: result.events?.length || 0
      });

      return result;
      
    } catch (error) {
      console.error('❌ Webhook verification error:', error);
      return {
        success: false,
        error: error.message || 'Failed to verify webhook processing',
        webhook_processed: false
      };
    }
  },

  /**
   * ROBUST ADDITION: Get comprehensive subscription debug info
   * For troubleshooting subscription issues
   */
  async getSubscriptionDebugInfo(email, sessionId = null) {
    console.log('🐛 Getting debug info for:', { email, sessionId });
    
    try {
      if (!email) {
        throw new Error('Email is required for debug info');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'get_debug_info',
          email: email,
          sessionId: sessionId
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Debug info retrieval failed');
      }

      console.log('🐛 Debug info retrieved:', result.debug_info);
      return result;
      
    } catch (error) {
      console.error('❌ Debug info error:', error);
      return {
        success: false,
        error: error.message || 'Failed to get debug info'
      };
    }
  },

  /**
   * ROBUST ADDITION: Manual subscription sync
   * Force sync subscription data from Stripe
   */
  async syncSubscriptionFromStripe(email) {
    console.log('🔄 Syncing subscription from Stripe for:', email);
    
    try {
      if (!email) {
        throw new Error('Email is required for subscription sync');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'sync_from_stripe',
          email: email
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Subscription sync failed');
      }

      console.log('✅ Subscription synced from Stripe:', result);
      return result;
      
    } catch (error) {
      console.error('❌ Subscription sync error:', error);
      return {
        success: false,
        error: error.message || 'Failed to sync subscription from Stripe'
      };
    }
  },

  /**
   * ROBUST ADDITION: Comprehensive subscription recovery
   * Attempts multiple methods to recover subscription data
   */
  async recoverSubscription(email, sessionId = null) {
    console.log('🩺 Starting comprehensive subscription recovery for:', email);
    
    const recoverySteps = [];
    
    try {
      // Step 1: Try normal status check
      recoverySteps.push('normal_status_check');
      console.log('🔍 Step 1: Normal status check...');
      const statusResult = await this.getSubscriptionStatus(email);
      
      if (statusResult.success && statusResult.subscription) {
        console.log('✅ Recovery Step 1 successful: Found via normal status check');
        return {
          success: true,
          subscription: statusResult.subscription,
          recoveryMethod: 'normal_status_check',
          recoverySteps
        };
      }

      // Step 2: Try with retry logic
      recoverySteps.push('retry_status_check');
      console.log('🔄 Step 2: Retry status check...');
      const retryResult = await this.getSubscriptionStatusWithRetry(email, 2);
      
      if (retryResult.success && retryResult.subscription) {
        console.log('✅ Recovery Step 2 successful: Found via retry');
        return {
          success: true,
          subscription: retryResult.subscription,
          recoveryMethod: 'retry_status_check',
          recoverySteps
        };
      }

      // Step 3: Try Stripe sync if still not found
      recoverySteps.push('stripe_sync');
      console.log('🔄 Step 3: Stripe sync...');
      const syncResult = await this.syncSubscriptionFromStripe(email);
      
      if (syncResult.success && syncResult.subscription) {
        console.log('✅ Recovery Step 3 successful: Found via Stripe sync');
        return {
          success: true,
          subscription: syncResult.subscription,
          recoveryMethod: 'stripe_sync',
          recoverySteps
        };
      }

      // Step 4: Try fallback creation if session ID provided
      if (sessionId) {
        recoverySteps.push('fallback_creation');
        console.log('🚨 Step 4: Fallback creation...');
        const fallbackResult = await this.createFallbackSubscription(email, sessionId);
        
        if (fallbackResult.success && fallbackResult.subscription) {
          console.log('✅ Recovery Step 4 successful: Created via fallback');
          return {
            success: true,
            subscription: fallbackResult.subscription,
            recoveryMethod: 'fallback_creation',
            recoverySteps
          };
        }
      }

      // All recovery methods failed
      console.error('❌ All recovery methods failed');
      return {
        success: false,
        error: 'All recovery methods failed',
        recoverySteps,
        subscription: null
      };
      
    } catch (error) {
      console.error('❌ Recovery process error:', error);
      return {
        success: false,
        error: error.message || 'Recovery process failed',
        recoverySteps,
        subscription: null
      };
    }
  },

  // =====================================================
  // EXISTING HELPER METHODS (UNCHANGED)
  // =====================================================

  /**
   * Check if user has active subscription
   */
  async hasActiveSubscription(email) {
    console.log('✅ Checking if user has active subscription:', email);
    
    try {
      const result = await this.getSubscriptionStatus(email);
      return result.success && result.hasActiveSubscription;
    } catch (error) {
      console.error('❌ Error checking active subscription:', error);
      return false;
    }
  },

  /**
   * Get user's current plan details
   */
  async getCurrentPlan(email) {
    console.log('📋 Getting current plan for:', email);
    
    try {
      const result = await this.getSubscriptionStatus(email);
      
      if (result.success && result.subscription) {
        return {
          success: true,
          plan: result.subscription.subscription_plans
        };
      }
      
      return {
        success: false,
        error: 'No active subscription found',
        plan: null
      };
    } catch (error) {
      console.error('❌ Error getting current plan:', error);
      return {
        success: false,
        error: error.message || 'Failed to get current plan',
        plan: null
      };
    }
  },

  // ADD THESE METHODS to your existing src/services/subscriptionService.js
// Place these at the end of the subscriptionService object, before the export

// =====================================================
// 🆕 NEW: ADMIN-SPECIFIC METHODS
// =====================================================

/**
 * Get all subscriptions for admin interface
 */
async getAllSubscriptions(filters = {}, search = '', limit = 100, offset = 0) {
  console.log('🔍 Getting all subscriptions for admin:', { filters, search });
  
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-subscription-queries`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        action: 'get_all_subscriptions',
        filters,
        search,
        limit,
        offset
      }),
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to fetch subscriptions');
    }

    console.log('✅ Admin subscriptions fetched:', result.data?.length || 0);
    return result;
    
  } catch (error) {
    console.error('❌ Error fetching admin subscriptions:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch subscriptions',
      data: [],
      totalCount: 0
    };
  }
},

/**
 * Get subscription analytics for admin dashboard
 */
async getSubscriptionAnalytics() {
  console.log('📈 Getting subscription analytics for admin');
  
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-subscription-queries`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        action: 'get_subscription_analytics'
      }),
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to fetch analytics');
    }

    console.log('✅ Subscription analytics fetched');
    return result;
    
  } catch (error) {
    console.error('❌ Error fetching subscription analytics:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch analytics',
      analytics: {
        totalSubscriptions: 0,
        activeSubscriptions: 0,
        monthlySubscriptions: 0,
        yearlySubscriptions: 0,
        totalRevenue: 0,
        averageRevenue: 0
      }
    };
  }
},

/**
 * Get detailed subscription information for specific user
 */
async getSubscriptionDetails(email) {
  console.log('🔍 Getting detailed subscription info for:', email);
  
  try {
    if (!email) {
      throw new Error('Email is required');
    }

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-subscription-queries`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        action: 'get_subscription_details',
        filters: { email }
      }),
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to fetch subscription details');
    }

    console.log('✅ Subscription details fetched for:', email);
    return result;
    
  } catch (error) {
    console.error('❌ Error fetching subscription details:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch subscription details',
      data: null
    };
  }
},

/**
 * Search subscriptions with advanced filters
 */
async searchSubscriptions(searchTerm, filters = {}) {
  console.log('🔍 Searching subscriptions:', { searchTerm, filters });
  
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-subscription-queries`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        action: 'search_subscriptions',
        search: searchTerm,
        filters
      }),
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to search subscriptions');
    }

    console.log('✅ Subscription search completed:', result.data?.length || 0, 'results');
    return result;
    
  } catch (error) {
    console.error('❌ Error searching subscriptions:', error);
    return {
      success: false,
      error: error.message || 'Failed to search subscriptions',
      data: [],
      count: 0
    };
  }
},

/**
 * Helper method to format subscription status for display
 */
formatSubscriptionStatus(status) {
  const statusMap = {
    'active': { label: 'Active', color: 'green' },
    'trialing': { label: 'Trial', color: 'blue' },
    'past_due': { label: 'Past Due', color: 'yellow' },
    'canceled': { label: 'Canceled', color: 'red' },
    'unpaid': { label: 'Unpaid', color: 'red' },
    'incomplete': { label: 'Incomplete', color: 'gray' },
    'paused': { label: 'Paused', color: 'gray' }
  };
  
  return statusMap[status] || { label: status, color: 'gray' };
},

/**
 * Helper method to calculate subscription metrics
 */
calculateSubscriptionMetrics(subscriptions) {
  if (!Array.isArray(subscriptions)) return {};
  
  const metrics = {
    total: subscriptions.length,
    active: 0,
    trialing: 0,
    canceled: 0,
    monthly: 0,
    yearly: 0,
    totalRevenue: 0,
    monthlyRevenue: 0,
    yearlyRevenue: 0
  };
  
  subscriptions.forEach(sub => {
    // Count by status
    if (sub.subscription_status === 'active') metrics.active++;
    if (sub.subscription_status === 'trialing') metrics.trialing++;
    if (sub.subscription_status === 'canceled') metrics.canceled++;
    
    // Count by plan type
    if (sub.subscription_plans?.billing_interval === 'month') metrics.monthly++;
    if (sub.subscription_plans?.billing_interval === 'year') metrics.yearly++;
    
    // Calculate revenue
    const revenue = sub.total_paid_cents || 0;
    metrics.totalRevenue += revenue;
    
    if (sub.subscription_status === 'active') {
      if (sub.subscription_plans?.billing_interval === 'month') {
        metrics.monthlyRevenue += sub.subscription_plans.price_cents || 0;
      } else if (sub.subscription_plans?.billing_interval === 'year') {
        metrics.yearlyRevenue += sub.subscription_plans.price_cents || 0;
      }
    }
  });
  
  return metrics;
},

/**
 * Helper method to generate Stripe dashboard URL
 */
getStripeCustomerUrl(customerId) {
  if (!customerId) return null;
  return `https://dashboard.stripe.com/customers/${customerId}`;
},

/**
 * Helper method to generate Stripe subscription URL
 */
getStripeSubscriptionUrl(subscriptionId) {
  if (!subscriptionId) return null;
  return `https://dashboard.stripe.com/subscriptions/${subscriptionId}`;
},


  /**
   * Validate subscription status
   */
  isValidSubscriptionStatus(status) {
    const validStatuses = ['active', 'trialing', 'past_due', 'canceled', 'unpaid', 'incomplete', 'paused'];
    return validStatuses.includes(status);
  },

  /**
   * Check if subscription is in good standing
   */
  isSubscriptionActive(subscription) {
    if (!subscription) return false;
    
    const goodStatuses = ['active', 'trialing'];
    const hasValidStatus = goodStatuses.includes(subscription.subscription_status);
    
    // Check if not expired
    const notExpired = !subscription.current_period_end || 
                      new Date(subscription.current_period_end) > new Date();
    
    return hasValidStatus && notExpired;
  },

  /**
   * 2-19-26
   */
  // =====================================================
  // 🆕 NEW: EVENT PASSES ADMIN METHODS
  // =====================================================

  /**
   * Get all event passes for admin interface
   */
  async getAllEventPasses(filters = {}, search = '', limit = 200, offset = 0) {
    console.log('🎫 Getting all event passes for admin:', { filters, search });
    
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-event-passes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'get_all_passes',
          filters,
          search,
          limit,
          offset
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to fetch event passes');
      }

      console.log('✅ Admin event passes fetched:', result.data?.length || 0);
      return result;
      
    } catch (error) {
      console.error('❌ Error fetching admin event passes:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch event passes',
        data: [],
        totalCount: 0
      };
    }
  },

  /**
   * Get event passes analytics for admin dashboard
   */
  async getEventPassesAnalytics() {
    console.log('📈 Getting event passes analytics for admin');
    
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-event-passes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'get_passes_analytics'
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to fetch analytics');
      }

      console.log('✅ Event passes analytics fetched');
      return result;
      
    } catch (error) {
      console.error('❌ Error fetching event passes analytics:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch analytics',
        analytics: {
          totalPasses: 0,
          unusedPasses: 0,
          usedPasses: 0,
          expiredPasses: 0,
          refundedPasses: 0,
          totalRevenue: 0,
          averagePrice: 0
        }
      };
    }
  }


};






export default subscriptionService;